"use client"

import {signOut, useSession} from "next-auth/react";
import {Avatar, AvatarImage} from "@/components/ui/avatar";
import IconDocument from "@/icons/document.svg";
import {Button} from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent, DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "@/components/ui/dropdown";
import {MdClear, MdContentCopy, MdLogout} from "react-icons/md";
import Link from "next/link";
import {VscDebug} from "react-icons/vsc";
import {useEffect, useState} from "react";
import {User} from "next-auth";
import {FaUserSlash} from "react-icons/fa6";
import {useTranslations} from "@/contexts/TranslationContext";
import {clearCache} from "@/api/actions/utils";
import ProtectedComponent from "@/components/custom/ProtectedComponent";
import {Checkbox} from "@/components/ui/checkbox";
import {useDebug} from "@/contexts/DebugContext";
import {SiPayloadcms} from "react-icons/si";
import {useFlags} from "@/contexts/FlagsContext";
import {cmsAccessFlag} from "@/flags";

interface UserProfileProps {
    locale: string;
    labels: {
        watchlist: string;
        logout: string;
    },
    env: string;
    entraIdIssuer: string
}

export function UserProfile({ labels, locale, env, entraIdIssuer  }: UserProfileProps) {
    const [open, setOpen] = useState(false);
    const [profile, setProfile] = useState<User | null>(null)
    const debug = useDebug();

    const { data: session } = useSession();

    const isProd = !(env === "local" || env === "dev" || env === "stag");

    const t = useTranslations();

    const flags = useFlags();

    useEffect(() => {
        if(!session?.user) return;
        setProfile(session.user);
    }, [session])

    function handleCopyAccessToken(e: React.MouseEvent) {
        navigator.clipboard.writeText(String(session?.accessToken))
    }

    async function handleSignOut() {
        await signOut();
    }

    async function handleAccountChange() {
        await signOut({ redirect: false });
        const logoutUrl =`https://login.microsoftonline.com/${entraIdIssuer}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(window.location.href)}&prompt=logout`

        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = logoutUrl;
        document.body.appendChild(iframe);

        setTimeout(() => {
            window.location.href = window.location.href;
        }, 2000)
    }

    return (
        <>
            <DropdownMenu open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger asChild>
                    <Button variant={"ghost"} className={"flex gap-4 items-center justify-center"}>
                        {profile?.name}
                        {profile?.image && (
                            profile.image !== "" ?
                                <Avatar>
                                    <AvatarImage src={profile?.image} />
                                </Avatar> :
                                <FaUserSlash className={"size-4"} />
                        )}
                    </Button>
                </DropdownMenuTrigger>
                {session?.user && (
                    <DropdownMenuContent>
                        <DropdownMenuGroup>
                            {!isProd && (
                                <DropdownMenuItem className={"flex justify-between gap-4"} onClick={handleCopyAccessToken}>
                                    Copy access token
                                    <MdContentCopy />
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem asChild>
                                <Link href={`/${locale}/projects`} className={"flex gap-2 justify-between"}>
                                    {labels.watchlist}
                                    <IconDocument />
                                </Link>
                            </DropdownMenuItem>
                            {flags.devPages && (
                                <Link
                                    key={"dev-link"}
                                    href={"/dev"}
                                    onClick={() => setOpen(false)}>
                                    <DropdownMenuItem className={"flex justify-between items-center hover:cursor cursor-pointer hover:bg-gray-100"}>
                                        Dev Pages
                                        <VscDebug />
                                    </DropdownMenuItem>
                                </Link>
                            )}
                            {!isProd && (
                                <DropdownMenuItem className={"flex justify-between hover:cursor cursor-pointer"} onClick={async () => {
                                    await clearCache(window.location.pathname);
                                    localStorage.clear();
                                }}>
                                    Clear Cache
                                    <MdClear className={"size-4"} />
                                </DropdownMenuItem>
                            )}
                            {flags.debugButton && (
                                <DropdownMenuItem className={"flex justify-between items-center w-full"} onClick={e => {e.preventDefault(); debug.setDebug(!debug.debug)}}>
                                    Debug
                                    <Checkbox checked={debug.debug} onCheckedChange={debug.setDebug} />
                                </DropdownMenuItem>
                            )}
                            {flags.cmsAccessFlag && (
                                <Link href={"/admin"}>
                                    <DropdownMenuItem className={"flex justify-between items-center cursor:pointer"}>
                                        CMS
                                        <SiPayloadcms />
                                    </DropdownMenuItem>
                                </Link>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className={"flex justify-between hover:cursor cursor-pointer"} onClick={handleSignOut}>
                                {t["userProfile.logout"]}
                                <MdLogout />
                            </DropdownMenuItem>
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                )}
            </DropdownMenu>
        </>
    )
}